import React, { useRef, useState } from 'react';
import Loader from '../../common/Loader';
import PavOcrDetailsButton from './PavOcrDetailsButton';
import { PAV_ATTACHMENT_DRAG_TYPE, PAV_PENDING_ATTACHMENT_STATUSES } from './pavAssetUtils';

const TileStatusOverlay = ({ attachment }) => {
    if (attachment.status === 'queued') return <span className='material-icons text-font12 text-white drop-shadow'>schedule</span>;
    if (attachment.status === 'uploading') return <Loader size='12' thickness='2' color='#ffffff' />;
    if (attachment.status === 'extracting') return <span className='material-icons animate-pulse text-font12 text-white drop-shadow'>document_scanner</span>;
    if (attachment.status === 'error') return <span className='material-icons text-font12 text-amber-300 drop-shadow' title={attachment.error || 'Could not read this image'}>warning</span>;
    return <span className='material-icons text-font12 text-emerald-300 drop-shadow'>check_circle</span>;
};

const UploaderTile = ({ attachment, assetIndex, onPreview, onRemove, onUseOcrValue, isFullScreen, isCompact }) => {
    const isPending = PAV_PENDING_ATTACHMENT_STATUSES.includes(attachment.status);
    const sizeClass = isFullScreen ? 'h-[6.5rem] w-[6.5rem]' : isCompact ? 'h-16 w-16' : 'h-[5rem] w-[5rem]';
    return (
        <div
            draggable={!isPending}
            onDragStart={(event) => {
                if (isPending) return;
                event.dataTransfer.setData(PAV_ATTACHMENT_DRAG_TYPE, attachment.id);
                event.dataTransfer.effectAllowed = 'move';
            }}
            className={`group relative ${sizeClass} shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-scogof8 shadow-sm ${isPending ? '' : 'cursor-grab active:cursor-grabbing'}`}
            title={isPending ? attachment.name : `${attachment.name} — drag onto an asset card to assign it`}
        >
            <img src={attachment.preview_url || attachment.url} alt={attachment.name} className='h-full w-full object-cover' />
            <button type='button' onClick={onPreview} className='absolute inset-0 z-10' aria-label={`Preview ${attachment.name}`} />
            <span className='absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-slate-900/80 to-transparent' />
            {isPending && <span className='absolute inset-0 bg-slate-900/25' />}
            {assetIndex !== undefined && (
                <span className='absolute left-1.5 top-1.5 z-10 rounded bg-scogoprimary px-1.5 py-px text-font10 font-bold text-white shadow ring-1 ring-white/70'>A{assetIndex + 1}</span>
            )}
            <span className='absolute bottom-1.5 left-1.5 z-10 flex items-center'>
                <TileStatusOverlay attachment={attachment} />
            </span>
            <PavOcrDetailsButton
                attachment={attachment}
                className='absolute bottom-1.5 right-1.5'
                size={isFullScreen ? 'lg' : 'sm'}
                onUseValue={onUseOcrValue && assetIndex !== undefined ? (field) => onUseOcrValue(field, attachment) : undefined}
            />
            <button
                type='button'
                disabled={isPending}
                onClick={onRemove}
                className={`absolute right-1.5 top-1.5 z-20 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-slate-900/75 text-white shadow ${isPending ? 'cursor-not-allowed opacity-40' : 'hover:bg-red-600'}`}
                title={isPending ? 'Wait for image processing to finish' : 'Remove image'}
            >
                <span className='material-icons text-font11'>close</span>
            </button>
        </div>
    );
};

// The empty drop zone is the whole screen in the full-screen view, but inside a
// modal it only needs to read as a card, so it keeps its composition and drops a
// size instead of stretching to fill the body.
const HERO_SIZES = {
    full: { box: 'min-h-[38vh] flex-1 p-8', icon: 'h-16 w-16 rounded-2xl', glyph: 'text-font30', title: 'text-font18', body: 'max-w-md text-font12', actions: 'mt-5', button: 'h-10 px-4 text-font12', note: 'mt-4' },
    modal: { box: 'min-h-[14rem] w-full p-5', icon: 'h-12 w-12 rounded-xl', glyph: 'text-font24', title: 'text-font14', body: 'max-w-sm text-font11', actions: 'mt-3.5', button: 'h-10 px-3.5 text-font11', note: 'mt-3' },
};

const UploaderProgress = ({ processedCount, totalCount }) => (
    <div className='flex items-center gap-2'>
        <Loader size='13' thickness='2' color='#3498DB' />
        <div className='h-1.5 w-20 overflow-hidden rounded-full bg-scogof2'>
            <div className='h-full rounded-full bg-scogobgsky transition-all duration-500' style={{ width: `${totalCount > 0 ? Math.max(Math.round((processedCount / totalCount) * 100), 8) : 8}%` }} />
        </div>
        <span className='whitespace-nowrap text-font10 font-semibold text-scogobgsky'>{processedCount}/{totalCount}</span>
    </div>
);

export default function PavImageUploader({ attachments, assetIndexByAttachmentId, assetCount = 0, disabled, isProcessing, processedCount, onAddFiles, onRemoveAttachment, onPreviewAttachment, onUseOcrValue, onAddAsset, isCompact, isFullScreen }) {
    const fileInputRef = useRef(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const [showAllTiles, setShowAllTiles] = useState(false);
    const isEmpty = attachments.length === 0;
    const isHeroEmpty = isEmpty && assetCount === 0 && !isCompact;
    const heroSize = isFullScreen ? HERO_SIZES.full : HERO_SIZES.modal;
    const tileClass = isFullScreen ? 'h-[6.5rem] w-[6.5rem]' : isCompact ? 'h-16 w-16' : 'h-[5rem] w-[5rem]';
    const visibleLimit = showAllTiles ? attachments.length : isCompact ? 3 : isFullScreen ? 12 : 8;
    const hiddenCount = Math.max(attachments.length - visibleLimit, 0);

    // Reassignment drags between asset cards pass over this zone; only real
    // file drags from outside the browser should trigger an upload here.
    const isReassignDrag = (event) => Array.from(event.dataTransfer?.types || []).includes(PAV_ATTACHMENT_DRAG_TYPE);
    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragActive(false);
        if (isReassignDrag(event)) return;
        if (!disabled) onAddFiles(event.dataTransfer?.files);
    };
    const dragProps = {
        onDragOver: (event) => { event.preventDefault(); if (!isReassignDrag(event)) setIsDragActive(true); },
        onDragLeave: () => setIsDragActive(false),
        onDrop: handleDrop,
    };

    return (
        <section className={isHeroEmpty ? 'flex flex-1 flex-col justify-center' : `overflow-hidden rounded-lg border transition-colors ${isDragActive ? 'border-scogobgsky bg-blue-50 ring-2 ring-blue-100' : isEmpty && isCompact ? 'border-dashed border-blue-200 bg-[#f8fbff]' : 'border-slate-200 bg-white'}`}>
            <input ref={fileInputRef} type='file' accept='image/*' multiple className='hidden' onChange={(event) => { onAddFiles(event.target.files); event.target.value = ''; }} />

            {isEmpty ? (
                isCompact ? (
                    <button
                        {...dragProps}
                        type='button'
                        disabled={disabled}
                        onClick={() => fileInputRef.current?.click()}
                        className={`flex w-full items-center gap-3 px-3.5 py-4 text-left transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-blue-50'}`}
                    >
                        <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-white text-scogoprimary shadow-sm'>
                            <span className='material-icons-outlined text-font16'>cloud_upload</span>
                        </span>
                        <span className='min-w-0 flex-1'>
                            <span className='block text-font11 font-semibold leading-4 text-scogo21'>Upload asset images</span>
                            <span className='block truncate text-font10 font-normal leading-4 text-slate-500'>Click or drop photos · OCR fills asset details</span>
                        </span>
                    </button>
                ) : assetCount === 0 ? (
                    <div
                        {...dragProps}
                        onClick={() => fileInputRef.current?.click()}
                        className={`group relative flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed text-center transition-all ${heroSize.box} ${isDragActive
                            ? 'border-scogobgsky bg-scogolightblue ring-2 ring-blue-200'
                            : 'border-scogobgsky/30 bg-gradient-to-b from-[#f7fbff] to-white shadow-sm hover:border-scogobgsky hover:from-scogolightblue/60'
                            }`}
                    >
                        <span className={`flex items-center justify-center border border-blue-100 bg-white text-scogobgsky shadow-sm ring-4 ring-blue-50 transition-transform duration-200 group-hover:scale-105 ${heroSize.icon}`}>
                            <span className={`material-icons-outlined ${heroSize.glyph}`}>cloud_upload</span>
                        </span>

                        <h2 className={`mt-3 font-semibold leading-tight text-scogo15 ${heroSize.title}`}>Upload asset photos to get started</h2>
                        <p className={`mt-1 font-normal leading-relaxed text-slate-500 ${heroSize.body}`}>
                            Drag and drop photos here, or choose them from your computer. OCR reads the nameplates and fills the asset details.
                        </p>

                        <div className={`flex flex-wrap items-center justify-center gap-2 ${heroSize.actions}`}>
                            <button
                                type='button'
                                disabled={disabled}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    fileInputRef.current?.click();
                                }}
                                className={`flex items-center justify-center gap-1.5 rounded-lg bg-scogoprimary font-semibold text-white shadow-sm transition-colors ${heroSize.button} ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-800'}`}
                            >
                                <span className='material-icons-outlined text-font16'>upload</span>
                                Choose images
                            </button>

                            {onAddAsset && (
                                <button
                                    type='button'
                                    disabled={disabled}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onAddAsset();
                                    }}
                                    className={`flex items-center justify-center gap-1 rounded-lg border border-slate-300 bg-white font-medium text-scogo21 shadow-sm transition-colors ${heroSize.button} ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-scogoprimary hover:bg-scogolightblue hover:text-scogoprimary'}`}
                                    title='Create an empty asset card to enter information manually'
                                >
                                    <span className='material-icons text-font14'>add</span>
                                    Add asset
                                </button>
                            )}
                        </div>

                        <p className={`flex items-center gap-1.5 text-font10 font-normal text-slate-400 ${heroSize.note}`}>
                            <span className='material-icons-outlined text-font13'>info</span>
                            PNG, JPG, JPEG or WEBP · multiple files supported
                        </p>
                    </div>
                ) : (
                    <div {...dragProps} className='flex items-center gap-3 bg-slate-50 p-3'>
                        <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-white text-scogobgsky shadow-sm'>
                            <span className='material-icons-outlined text-xl'>add_photo_alternate</span>
                        </span>
                        <div className='min-w-0 flex-1'>
                            <p className='text-font12 font-semibold text-scogo21'>Add asset photos</p>
                            <p className='mt-0.5 text-font11 font-normal text-slate-500'>Upload one or more clear photos. OCR will group matching images and fill the asset details.</p>
                        </div>
                        <div className='flex shrink-0 items-center gap-2'>
                            <button type='button' disabled={disabled} onClick={() => fileInputRef.current?.click()} className={`flex h-11 items-center justify-center gap-1.5 rounded-lg bg-scogoprimary px-4 text-font12 font-semibold text-white shadow-sm transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-800'}`}>
                                <span className='material-icons-outlined text-font16'>upload</span>
                                Choose images
                            </button>
                        </div>
                    </div>
                )
            ) : (
                <div {...dragProps} className='p-3'>
                    <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
                        <p className='flex items-center gap-1.5 text-font12 font-medium text-scogo21'>
                            <span className='material-icons-outlined text-font16 text-slate-500'>photo_library</span>
                            {attachments.length} image{attachments.length === 1 ? '' : 's'}
                        </p>
                        {isProcessing
                            ? <UploaderProgress processedCount={processedCount} totalCount={attachments.length} />
                            : <span className='inline-flex items-center gap-1 text-font11 font-medium text-emerald-600'><span className='material-icons text-font13'>auto_awesome</span>{assetCount} asset{assetCount === 1 ? '' : 's'} detected</span>}
                    </div>
                    <div className='flex flex-wrap items-center gap-2'>
                        {attachments.slice(0, visibleLimit).map((attachment) => (
                            <UploaderTile
                                key={attachment.id}
                                attachment={attachment}
                                assetIndex={assetIndexByAttachmentId.get(attachment.id)}
                                onUseOcrValue={onUseOcrValue}
                                onPreview={() => onPreviewAttachment(attachment.id)}
                                onRemove={() => onRemoveAttachment(attachment.id)}
                                isFullScreen={isFullScreen}
                                isCompact={isCompact}
                            />
                        ))}
                        {hiddenCount > 0 && (
                            <button type='button' onClick={() => setShowAllTiles(true)} title={`Show all ${attachments.length} images`} className={`flex ${tileClass} shrink-0 flex-col items-center justify-center rounded-lg border border-slate-200 bg-scogof8 text-scogoprimary transition-colors hover:border-scogoprimary hover:bg-scogolightblue`}>
                                <span className='text-font11 font-bold'>+{hiddenCount}</span>
                                <span className='text-font10 text-slate-500'>more</span>
                            </button>
                        )}
                        <button type='button' disabled={disabled} onClick={() => fileInputRef.current?.click()} className={`flex ${tileClass} shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-slate-300 bg-white text-slate-500 transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-scogoprimary hover:bg-scogolightblue hover:text-scogoprimary'}`}>
                            <span className='material-icons-outlined text-font18'>add_photo_alternate</span>
                            <span className='text-font10 font-medium'>Add</span>
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
